import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { toast } from 'sonner';
import {
  Receipt,
  TrendingUp,
  CheckCircle,
  Clock,
  Loader2,
  FileText,
  Percent,
  Save,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCommissions() {
  useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [newRate, setNewRate] = useState('');
  const [savingRate, setSavingRate] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/commissions/admin/overview`);
      setData(res.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerateInvoices = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/commissions/admin/generate-invoices`, {});
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur'));
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveRate = async (restaurantId) => {
    const rate = parseFloat(newRate) / 100;
    if (isNaN(rate) || rate < 0 || rate > 0.30) {
      toast.error('Taux invalide (entre 0% et 30%)');
      return;
    }
    setSavingRate(true);
    try {
      await axios.put(`${API}/commissions/admin/rate/${restaurantId}`,
        { commission_rate: rate }
      );
      toast.success('Taux mis a jour');
      setEditingRate(null);
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur'));
    } finally {
      setSavingRate(false);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    setMarkingPaid(invoiceId);
    try {
      await axios.post(`${API}/commissions/admin/mark-paid/${invoiceId}`, {});
      toast.success('Facture marquee comme payee');
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur'));
    } finally {
      setMarkingPaid(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Commissions">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </AdminLayout>
    );
  }

  const restaurants = data?.by_restaurant || [];
  const invoices = data?.recent_invoices || [];

  return (
    <AdminLayout title="Commissions">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commissions</h1>
          <p className="text-sm text-gray-500 mt-1">Gestion des commissions et facturation</p>
        </div>
        <Button
          onClick={handleGenerateInvoices}
          disabled={generating}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="generate-invoices-btn"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileText className="w-4 h-4 mr-2" />
          )}
          Generer les factures
        </Button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card data-testid="admin-stat-pending">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Commissions en attente</CardTitle>
            <Clock className="w-5 h-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{data?.total_pending?.toFixed(2)} EUR</div>
            <p className="text-xs text-gray-500 mt-1">{data?.count_pending} commission(s) non facturees</p>
          </CardContent>
        </Card>

        <Card data-testid="admin-stat-paid">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total encaisse</CardTitle>
            <CheckCircle className="w-5 h-5 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{data?.total_paid?.toFixed(2)} EUR</div>
            <p className="text-xs text-gray-500 mt-1">Factures payees</p>
          </CardContent>
        </Card>

        <Card data-testid="admin-stat-restaurants">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Commerces avec commissions</CardTitle>
            <TrendingUp className="w-5 h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{restaurants.length}</div>
            <p className="text-xs text-gray-500 mt-1">Commissions en attente</p>
          </CardContent>
        </Card>
      </div>

      {/* By Restaurant */}
      <Card data-testid="commissions-by-restaurant">
        <CardHeader>
          <CardTitle className="text-base">Commissions par commerce</CardTitle>
          <CardDescription>Taux configurable individuellement, commissions en attente de facturation</CardDescription>
        </CardHeader>
        <CardContent>
          {restaurants.length === 0 ? (
            <p className="text-center text-gray-500 py-6 text-sm">Aucune commission en attente</p>
          ) : (
            <div className="space-y-3">
              {restaurants.map((r) => (
                <div key={r.restaurant_id} className="flex items-center justify-between p-4 border rounded-xl" data-testid={`restaurant-commission-${r.restaurant_id}`}>
                  <div className="flex-1">
                    <p className="font-medium">{r.restaurant_name}</p>
                    <p className="text-sm text-gray-500">{r.count} commande(s) - {(Number(r.total) || 0).toFixed(2)} EUR dus</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {editingRate === r.restaurant_id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="30"
                          step="0.5"
                          value={newRate}
                          onChange={(e) => setNewRate(e.target.value)}
                          className="w-20 h-9"
                          data-testid={`rate-input-${r.restaurant_id}`}
                        />
                        <span className="text-sm text-gray-500">%</span>
                        <Button
                          size="sm"
                          onClick={() => handleSaveRate(r.restaurant_id)}
                          disabled={savingRate}
                          data-testid={`save-rate-${r.restaurant_id}`}
                        >
                          {savingRate ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingRate(null)}>X</Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setEditingRate(r.restaurant_id); setNewRate(String(r.commission_rate * 100)); }}
                        data-testid={`edit-rate-${r.restaurant_id}`}
                      >
                        <Percent className="w-3 h-3 mr-1" />
                        {(r.commission_rate * 100).toFixed(1)}%
                      </Button>
                    )}
                    <Badge className="bg-amber-100 text-amber-700 font-bold">{(Number(r.total) || 0).toFixed(2)} EUR</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card data-testid="admin-invoices-list">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Factures recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-center text-gray-500 py-6 text-sm">Aucune facture generee</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-3 font-medium">Commerce</th>
                    <th className="pb-3 font-medium">Periode</th>
                    <th className="pb-3 font-medium">Commandes</th>
                    <th className="pb-3 font-medium">Montant</th>
                    <th className="pb-3 font-medium">Statut</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b last:border-0">
                      <td className="py-3 font-medium">{inv.restaurant_name}</td>
                      <td className="py-3 text-gray-500">
                        {new Date(inv.period_start).toLocaleDateString('fr-FR')} - {new Date(inv.period_end).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-3">{inv.commission_count}</td>
                      <td className="py-3 font-semibold">{inv.total_amount?.toFixed(2)} EUR</td>
                      <td className="py-3">
                        {inv.status === 'paid' ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Payee</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
                        )}
                      </td>
                      <td className="py-3">
                        {inv.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarkPaid(inv.id)}
                            disabled={markingPaid === inv.id}
                            data-testid={`mark-paid-${inv.id}`}
                          >
                            {markingPaid === inv.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Marquer payee
                              </>
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </AdminLayout>
  );
}
